import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { billingAPI } from '../api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Crown, Download, Zap, Headphones,
  CheckCircle2, ShieldCheck, Lock, Loader2, Clock, Settings, Gift
} from 'lucide-react';

const PlanPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const trialsEnabled = useFeatureFlag('enable_premium_trials');
  const [searchParams] = useSearchParams();
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [startingTrial, setStartingTrial] = useState(false);
  const [billingStatus, setBillingStatus] = useState(null);
  const [loadingBilling, setLoadingBilling] = useState(true);

  const tier = user?.plan_tier || 'free';
  const isPremium = tier === 'premium';
  // isEffectivePremium: accounts for both Stripe premium AND active trial
  const trialActive = billingStatus?.trial_active || user?.trial_active || false;
  const isEffectivePremium = isPremium || trialActive;
  const peerStatus = user?.peer_verification_status || 'none';
  const trialEnds = billingStatus?.trial_expires_at || user?.trial_expires_at || user?.trial_ends_at;
  const trialDaysLeft = billingStatus?.days_remaining != null
    ? billingStatus.days_remaining
    : (trialEnds ? Math.max(0, Math.ceil((new Date(trialEnds).getTime() - Date.now()) / 86400000)) : 0);
  const trialUsed = billingStatus?.trial_used || user?.trial_used || false;
  const hasSub = billingStatus?.subscription_status === 'active' || user?.has_subscription;

  useEffect(() => {
    loadBillingStatus();
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const billingResult = searchParams.get('billing');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else if (billingResult === 'success') {
      toast.success('Payment successful! Refreshing your plan...');
      setTimeout(() => window.location.href = '/plan', 1500);
    } else if (billingResult === 'cancel') {
      toast('Checkout canceled.');
    }
  }, []);

  const loadBillingStatus = async () => {
    try {
      const data = await billingAPI.getBillingStatus();
      setBillingStatus(data);
    } catch {
      // Billing endpoint may not be available
    } finally {
      setLoadingBilling(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) { setCheckingPayment(false); return; }
    setCheckingPayment(true);
    try {
      const data = await billingAPI.getCheckoutStatus(sessionId);
      if (data.payment_status === 'paid') {
        toast.success('Payment successful! You now have Pro access.');
        setCheckingPayment(false);
        window.location.href = '/plan';
        return;
      }
      if (data.status === 'expired') {
        toast.error('Session expired.');
        setCheckingPayment(false);
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch { setCheckingPayment(false); }
  };

  const handleStartTrial = async () => {
    setStartingTrial(true);
    try {
      await billingAPI.startTrial();
      toast.success('30-day Pro trial started! All Pro features are now unlocked.');
      // Refresh both billing status and user capabilities
      await Promise.all([loadBillingStatus(), refreshUser()]);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail?.error_code === 'trial_already_used') {
        toast.error('You have already used your free trial.');
        await loadBillingStatus();
      } else if (detail?.error_code === 'already_premium') {
        toast('You already have Pro access.');
      } else if (detail?.error_code === 'feature_disabled') {
        toast.error('Free trials are not available yet.');
      } else {
        toast.error('Failed to start trial. Please try again.');
      }
    } finally {
      setStartingTrial(false);
    }
  };

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      const originUrl = window.location.origin;
      const { url } = await billingAPI.createCheckout(originUrl);
      window.location.href = url;
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail?.error_code === 'billing_not_configured') {
        toast('Billing not available yet. Contact support to upgrade.');
      } else {
        toast.error('Failed to start checkout.');
      }
      setSubscribing(false);
    }
  };

  const handleManagePlan = async () => {
    try {
      const originUrl = window.location.origin;
      const { url } = await billingAPI.createPortalSession(originUrl);
      window.location.href = url;
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail?.error_code === 'portal_unavailable') {
        toast('Plan management is not available yet. Contact support.');
      } else {
        toast.error('Failed to open subscription management.');
      }
    }
  };

  const billingEnabled = billingStatus?.billing_enabled || false;
  const cancelAtPeriodEnd = billingStatus?.cancel_at_period_end;
  const portalAvailable = billingStatus?.portal_available === true;

  const benefits = [
    { icon: Download, label: 'Library Export (CSV & RIS)', available: isEffectivePremium },
    { icon: Zap, label: 'Higher digest limits (25 articles)', available: isEffectivePremium },
    { icon: Zap, label: '5 Run Now requests per day', available: isEffectivePremium },
    { icon: Zap, label: '1,000 library articles', available: isEffectivePremium },
    { icon: Headphones, label: 'Audio takeaways & Daily Briefing', available: isEffectivePremium },
  ];

  // Determine what CTA to show
  const showTrialCTA = trialsEnabled && !trialUsed && !isEffectivePremium && !hasSub;
  const showTrialActive = trialsEnabled && trialActive && !hasSub;
  const showTrialEnded = trialsEnabled && trialUsed && !trialActive && !isPremium && !hasSub;

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="plan-page">
      <div className="max-w-2xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />Back
        </Button>

        {checkingPayment && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 flex items-center gap-3" data-testid="payment-checking">
            <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
            <span className="text-sm text-blue-800">Confirming your payment...</span>
          </div>
        )}

        <Card>
          <CardHeader className="text-center">
            <Crown className={`h-8 w-8 mx-auto mb-2 ${isEffectivePremium ? 'text-amber-500' : 'text-gray-400'}`} />
            <CardTitle className="text-2xl">Your Plan</CardTitle>
            <CardDescription>Manage your LitCurrent subscription</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Tier + status badges */}
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Badge
                className={isEffectivePremium ? 'bg-amber-100 text-amber-800 text-sm px-3 py-1' : 'bg-gray-100 text-gray-700 text-sm px-3 py-1'}
                data-testid="plan-tier-badge"
              >
                {isEffectivePremium ? 'Pro' : 'Free'}
              </Badge>
              {showTrialActive && (
                <Badge className="bg-blue-100 text-blue-700 text-sm px-3 py-1" data-testid="trial-active-badge">
                  <Clock className="h-3 w-3 mr-1 inline" />
                  Trial: {trialDaysLeft}d left
                </Badge>
              )}
              {cancelAtPeriodEnd && (
                <Badge className="bg-red-100 text-red-700 text-sm px-3 py-1">Cancels at period end</Badge>
              )}
              <Badge
                className={peerStatus === 'verified' ? 'bg-emerald-100 text-emerald-700 text-sm px-3 py-1' : 'bg-gray-100 text-gray-600 text-sm px-3 py-1'}
                data-testid="verification-status-badge"
              >
                {peerStatus === 'verified' ? 'Verified' : peerStatus === 'pending' ? 'Pending' : 'Unverified'}
              </Badge>
            </div>

            {/* Trial active banner */}
            {showTrialActive && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center" data-testid="trial-active-banner">
                <p className="text-sm text-blue-800">
                  You're on a <strong>30-day Pro trial</strong> — <strong>{trialDaysLeft}</strong> day{trialDaysLeft !== 1 ? 's' : ''} remaining.
                  All Pro features are unlocked.
                </p>
              </div>
            )}

            {/* Trial ended banner */}
            {showTrialEnded && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center" data-testid="trial-ended-banner">
                <p className="text-sm text-amber-800">
                  Your free trial has ended. Subscribe to continue using Pro features.
                </p>
              </div>
            )}

            {/* Benefits list */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-900">Pro benefits</h3>
              {benefits.map((b, i) => (
                <div key={i} className="flex items-center gap-3 py-2">
                  {b.available ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
                  ) : (
                    <Lock className="h-5 w-5 text-gray-300 shrink-0" />
                  )}
                  <span className={`text-sm ${b.available ? 'text-gray-900' : 'text-gray-500'}`}>{b.label}</span>
                </div>
              ))}
            </div>

            {/* CTA section */}
            {hasSub ? (
              /* Active subscription */
              <div className="space-y-3">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center" data-testid="active-subscription-banner">
                  <p className="text-sm text-amber-800">You have an active Pro subscription.</p>
                  {billingStatus?.current_period_end && (
                    <p className="text-xs text-amber-600 mt-1">
                      Current period ends: {new Date(billingStatus.current_period_end).toLocaleDateString()}
                    </p>
                  )}
                </div>
                {portalAvailable && billingEnabled && billingStatus?.has_customer && (
                  <Button variant="outline" size="sm" className="w-full" onClick={handleManagePlan} data-testid="manage-plan-btn">
                    <Settings className="h-4 w-4 mr-1.5" />Manage Plan
                  </Button>
                )}
              </div>
            ) : showTrialCTA ? (
              /* Trial available */
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-5 text-center space-y-3" data-testid="trial-cta">
                <Gift className="h-8 w-8 mx-auto text-blue-600" />
                <p className="text-lg font-semibold text-gray-900">Try Pro free for 30 days</p>
                <p className="text-sm text-gray-600">No credit card required. All Pro features unlocked instantly.</p>
                <Button
                  onClick={handleStartTrial}
                  disabled={startingTrial}
                  className="bg-blue-600 hover:bg-blue-700 w-full"
                  data-testid="start-trial-btn"
                >
                  {startingTrial
                    ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Starting trial...</>
                    : <><Gift className="h-4 w-4 mr-1.5" />Start 30-day Pro trial (no credit card)</>
                  }
                </Button>
                <p className="text-xs text-gray-500">Billing coming soon. Trial ends after 30 days.</p>
              </div>
            ) : showTrialActive ? (
              /* Trial active — nudge toward future subscription */
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center space-y-2" data-testid="trial-active-cta">
                <p className="text-sm text-blue-800 font-medium">Enjoying Pro?</p>
                <p className="text-xs text-blue-600">Billing will be available soon. Your trial ends in {trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''}.</p>
              </div>
            ) : showTrialEnded ? (
              /* Trial ended — billing coming soon */
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-5 text-center" data-testid="trial-ended-cta">
                <p className="text-lg font-semibold text-gray-900 mb-1">LitCurrent Pro</p>
                <p className="text-3xl font-bold text-gray-900 mb-1">$4.99<span className="text-base font-normal text-gray-500">/month</span></p>
                <p className="text-xs text-gray-500 mb-4">Cancel anytime.</p>
                <Button size="sm" variant="outline" disabled data-testid="billing-soon-btn">
                  <Crown className="h-4 w-4 mr-1.5" />Billing coming soon
                </Button>
              </div>
            ) : (
              /* No trial feature — standard subscribe CTA */
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-5 text-center" data-testid="subscribe-cta">
                <p className="text-lg font-semibold text-gray-900 mb-1">LitCurrent Pro</p>
                <p className="text-3xl font-bold text-gray-900 mb-1">$4.99<span className="text-base font-normal text-gray-500">/month</span></p>
                <p className="text-xs text-gray-500 mb-4">Cancel anytime.</p>
                {billingEnabled ? (
                  <Button onClick={handleSubscribe} disabled={subscribing} className="bg-amber-600 hover:bg-amber-700" data-testid="subscribe-btn">
                    {subscribing ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Processing...</> : <><Crown className="h-4 w-4 mr-1.5" />Subscribe to Pro</>}
                  </Button>
                ) : (
                  <Button size="sm" onClick={() => alert('Contact admin@litpulse.com to request Pro access.')} data-testid="request-pro-btn">
                    <Crown className="h-4 w-4 mr-1.5" />Request Pro Access
                  </Button>
                )}
              </div>
            )}

            {/* Verification */}
            {peerStatus !== 'verified' && (
              <div className="border-t pt-4">
                <Button variant="outline" size="sm" className="w-full" onClick={() => navigate('/verification')}>
                  <ShieldCheck className="h-4 w-4 mr-1.5" />Get Verified
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PlanPage;
